import {
  ApolloClient,
  createHttpLink,
  DefaultOptions,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  ApolloLink,
  ServerError,
} from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { setContext } from "@apollo/client/link/context";
import App from "App";
import { onError } from "@apollo/client/link/error";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import sessonManager from "utils/sessionManager";
import { useCallback, useMemo } from "react";
import { CustomRestLink } from "./CustomRestLink";
import { BOOKING_JOURNEY } from "utils/constants";
import { tenantSettings } from "utils/authentication/tenantSettings";
import { GetApolloLinkWithToken } from "./AuthenticationHelpers";
import { ErrorBoundary } from "components/error-boundary/error-boundary";

export const OptimalApolloConnection = (): React.ReactElement => {
  const auth = useAuth();
  const AsyncTokenLookup = async () => {
    if (auth.isAuthenticated) {
      const user = auth.user;
      return {
        accessToken: user?.access_token,
        idToken: user?.id_token,
      };
    } else {
      auth.signinRedirect();
      return null;
    }
  };

  const withToken = setContext(async (request, { headers }) => {
    const authenticationResult = await AsyncTokenLookup();
    return GetApolloLinkWithToken(authenticationResult, request, headers);
  });

  const restLink = CustomRestLink({
    uri: process.env.REACT_APP_IBF_URL || "",
  });

  const httpLink = createHttpLink({
    uri: tenantSettings.customConfig.api.baseUrl,
  });

  const navigate = useNavigate();

  const errorLink = onError(({ networkError }: any) => {
    if (networkError) {
      if ((networkError as ServerError)?.statusCode === 403) {
        navigate("/unauthorized");
        console.warn(`server returned 403`);
      } else {
        console.log(
          `[Network error]: ${networkError}, isFromIframe: ${
            window.location !== window.parent.location
          }`,
        );
      }
    }
  });

  const defaultOptions: DefaultOptions = {
    watchQuery: {
      fetchPolicy: "no-cache",
    },
    query: {
      fetchPolicy: "no-cache",
    },
  };

  // Custom link to append operation name to URI for GraphQL operations
  const appendOperationNameLink = new ApolloLink((operation, forward) => {
    operation.setContext({
      uri: `${tenantSettings.customConfig.api.baseUrl}?${operation.operationName}`,
    });
    return forward ? forward(operation) : null;
  });

  const client = useMemo<any>(() => {
    return new ApolloClient({
      cache: new InMemoryCache({}),
      link: from([
        errorLink as unknown as ApolloLink,
        withToken,
        restLink,
        appendOperationNameLink,
        httpLink,
      ]),
      defaultOptions: defaultOptions,
    });
  }, []);

  const onSilentRenew = async () => {
    await auth.signinSilent();
    sessonManager.updateTokenLastFetchedAt();
  };

  const onLogout = async () => {
    tenantSettings.authService.signOut();
  };

  const onSilenRenewCallback = useCallback(onSilentRenew, []);
  const onLogoutCallback = useCallback(onLogout, []);
  return (
    <ApolloProvider client={client}>
      <ErrorBoundary>
        <App
          onSilentRenew={onSilenRenewCallback}
          isAuthenticated={true}
          onLogout={onLogoutCallback}
        />
      </ErrorBoundary>
    </ApolloProvider>
  );
};
